import React from 'react';
import styled from 'styled-components';
import { motion } from 'framer-motion';
import { useNavigate } from 'react-router-dom';


const Container = styled(motion.div)`
  min-height: 100vh;
  background: linear-gradient(135deg, #0f0f0f 0%, #1a1a1a 100%);
  color: white;
  padding: 20px;
  padding-top: 80px;
`;

const Header = styled.div`
  display: flex;
  align-items: center;
  margin-bottom: 40px;
  max-width: 800px;
  margin: 0 auto 40px auto;
`;

const BackButton = styled(motion.button)`
  background: rgba(255, 255, 255, 0.1);
  border: none;
  color: white;
  padding: 12px;
  border-radius: 50%;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  margin-right: 20px;
  transition: all 0.3s ease;
  font-size: 18px;
  font-weight: bold;
  
  &:hover {
    background: rgba(255, 255, 255, 0.2);
    transform: scale(1.1);
  }
`;

const Title = styled.h1`
  font-size: 2.5rem;
  font-weight: 700;
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
  margin: 0;
`;

const Content = styled.div`
  max-width: 800px;
  margin: 0 auto;
  background: rgba(255, 255, 255, 0.05);
  border-radius: 20px;
  padding: 40px;
  backdrop-filter: blur(10px);
  border: 1px solid rgba(255, 255, 255, 0.1);
`;

const Section = styled.div`
  margin-bottom: 30px;
`;

const SectionTitle = styled.h2`
  font-size: 1.5rem;
  font-weight: 600;
  color: #667eea;
  margin-bottom: 15px;
  border-bottom: 2px solid rgba(102, 126, 234, 0.3);
  padding-bottom: 10px;
`;

const Paragraph = styled.p`
  font-size: 1rem;
  line-height: 1.6;
  color: #e0e0e0;
  margin-bottom: 15px;
  text-align: justify;
`;

const List = styled.ul`
  margin: 15px 0;
  padding-left: 20px;
`;

const ListItem = styled.li`
  font-size: 1rem;
  line-height: 1.6;
  color: #e0e0e0;
  margin-bottom: 8px;
`;

const Highlight = styled.span`
  color: #667eea;
  font-weight: 600;
`;

const DataTable = styled.div`
  background: rgba(255, 255, 255, 0.05);
  border-radius: 10px;
  padding: 20px;
  margin: 15px 0;
  border: 1px solid rgba(255, 255, 255, 0.1);
`;

const DataRow = styled.div`
  display: flex;
  justify-content: space-between;
  padding: 10px 0;
  border-bottom: 1px solid rgba(255, 255, 255, 0.1);
  
  &:last-child {
    border-bottom: none;
  }
`;

const DataLabel = styled.span`
  font-weight: 600;
  color: #667eea;
  flex: 1;
`;

const DataPurpose = styled.span`
  color: #e0e0e0;
  flex: 2;
  text-align: right;
`;

const LastUpdated = styled.div`
  text-align: center;
  margin-top: 40px;
  padding: 20px;
  background: rgba(102, 126, 234, 0.1);
  border-radius: 10px;
  border: 1px solid rgba(102, 126, 234, 0.2);
`;

const LastUpdatedText = styled.p`
  color: #667eea;
  font-size: 0.9rem;
  margin: 0;
`;

const PrivacyPolicy = () => {
  const navigate = useNavigate();

  const handleBack = () => {
    navigate(-1);
  };

  return (
    <Container
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      transition={{ duration: 0.5 }}
    >
      <Header>
        <BackButton
          onClick={handleBack}
          whileHover={{ scale: 1.1 }}
          whileTap={{ scale: 0.9 }}
        >
          ←
        </BackButton>
        <Title>Privacy Policy</Title>
      </Header>

      <Content>
        <Section>
          <SectionTitle>1. Introduction</SectionTitle>
          <Paragraph>
            ChefKats.AI ("we," "our," or "us") is committed to protecting your privacy. This Privacy Policy explains how we collect, use, disclose, and safeguard your information when you use our AI-powered recipe generation service.
          </Paragraph>
          <Paragraph>
            By using ChefKats.AI, you consent to the data practices described in this policy. If you do not agree with our policies and practices, please do not use our service.
          </Paragraph>
        </Section>

        <Section>
          <SectionTitle>2. Information We Collect</SectionTitle>
          <Paragraph>
            We collect several types of information from and about users of our service:
          </Paragraph>
          
          <DataTable>
            <DataRow>
              <DataLabel>Personal Information</DataLabel>
              <DataPurpose>Name, email, profile preferences</DataPurpose>
            </DataRow>
            <DataRow>
              <DataLabel>Usage Data</DataLabel>
              <DataPurpose>Recipe searches, cooking preferences, app interactions</DataPurpose>
            </DataRow>
            <DataRow>
              <DataLabel>Device Information</DataLabel>
              <DataPurpose>Device type, operating system, browser type</DataPurpose>
            </DataRow>
            <DataRow>
              <DataLabel>Location Data</DataLabel>
              <DataPurpose>General location for regional recipe suggestions</DataPurpose>
            </DataRow>
          </DataTable>
        </Section>

        <Section>
          <SectionTitle>3. How We Use Your Information</SectionTitle>
          <Paragraph>
            We use the information we collect to:
          </Paragraph>
          <List>
            <ListItem>Provide and maintain our AI recipe generation service</ListItem>
            <ListItem>Personalize your cooking experience and recommendations</ListItem>
            <ListItem>Improve our AI algorithms and service quality</ListItem>
            <ListItem>Communicate with you about updates and features</ListItem>
            <ListItem>Ensure security and prevent fraud</ListItem>
            <ListItem>Comply with legal obligations</ListItem>
          </List>
        </Section>

        <Section>
          <SectionTitle>4. AI and Machine Learning</SectionTitle>
          <Paragraph>
            Our service uses artificial intelligence and machine learning to provide personalized recipe recommendations. This involves:
          </Paragraph>
          <List>
            <ListItem>Analyzing your cooking preferences and dietary restrictions</ListItem>
            <ListItem>Learning from your recipe selections and ratings</ListItem>
            <ListItem>Processing ingredient combinations and nutritional data</ListItem>
            <ListItem>Improving recommendation accuracy over time</ListItem>
          </List>
          <Paragraph>
            <Highlight>Note:</Highlight> We do not use your personal data to train third-party AI models or sell your information to other companies.
          </Paragraph>
        </Section>

        <Section>
          <SectionTitle>5. Data Sharing and Disclosure</SectionTitle>
          <Paragraph>
            We do not sell, trade, or otherwise transfer your personal information to third parties, except in the following circumstances:
          </Paragraph>
          <List>
            <ListItem><Highlight>Service Providers:</Highlight> We may share data with trusted third-party service providers who assist us in operating our service</ListItem>
            <ListItem><Highlight>Legal Requirements:</Highlight> We may disclose information if required by law or to protect our rights and safety</ListItem>
            <ListItem><Highlight>Business Transfers:</Highlight> In case of merger, acquisition, or sale of assets, user information may be transferred</ListItem>
            <ListItem><Highlight>Consent:</Highlight> We may share information with your explicit consent</ListItem>
          </List>
        </Section>

        <Section>
          <SectionTitle>6. Data Security</SectionTitle>
          <Paragraph>
            We implement appropriate technical and organizational security measures to protect your personal information, including:
          </Paragraph>
          <List>
            <ListItem>Encryption of data in transit and at rest</ListItem>
            <ListItem>Regular security assessments and updates</ListItem>
            <ListItem>Access controls and authentication measures</ListItem>
            <ListItem>Secure data storage and backup procedures</ListItem>
          </List>
          <Paragraph>
            However, no method of transmission over the internet is 100% secure, and we cannot guarantee absolute security.
          </Paragraph>
        </Section>

        <Section>
          <SectionTitle>7. Data Retention</SectionTitle>
          <Paragraph>
            We retain your personal information for as long as necessary to:
          </Paragraph>
          <List>
            <ListItem>Provide our services to you</ListItem>
            <ListItem>Comply with legal obligations</ListItem>
            <ListItem>Resolve disputes and enforce agreements</ListItem>
            <ListItem>Improve our service quality</ListItem>
          </List>
          <Paragraph>
            You may request deletion of your account and associated data at any time through your account settings or by contacting us.
          </Paragraph>
        </Section>

        <Section>
          <SectionTitle>8. Your Rights and Choices</SectionTitle>
          <Paragraph>
            You have the following rights regarding your personal information:
          </Paragraph>
          <List>
            <ListItem><Highlight>Access:</Highlight> Request a copy of your personal data</ListItem>
            <ListItem><Highlight>Correction:</Highlight> Update or correct inaccurate information</ListItem>
            <ListItem><Highlight>Deletion:</Highlight> Request deletion of your personal data</ListItem>
            <ListItem><Highlight>Portability:</Highlight> Request transfer of your data to another service</ListItem>
            <ListItem><Highlight>Opt-out:</Highlight> Unsubscribe from marketing communications</ListItem>
            <ListItem><Highlight>Restriction:</Highlight> Limit how we process your data</ListItem>
          </List>
        </Section>

        <Section>
          <SectionTitle>9. Cookies and Tracking Technologies</SectionTitle>
          <Paragraph>
            We use cookies and similar tracking technologies to:
          </Paragraph>
          <List>
            <ListItem>Remember your preferences and settings</ListItem>
            <ListItem>Analyze service usage and performance</ListItem>
            <ListItem>Provide personalized content and recommendations</ListItem>
            <ListItem>Improve user experience and service functionality</ListItem>
          </List>
          <Paragraph>
            You can control cookie settings through your browser preferences, though disabling cookies may affect service functionality.
          </Paragraph>
        </Section>

        <Section>
          <SectionTitle>10. Children's Privacy</SectionTitle>
          <Paragraph>
            Our service is not intended for children under 13 years of age. We do not knowingly collect personal information from children under 13. If you are a parent or guardian and believe your child has provided us with personal information, please contact us immediately.
          </Paragraph>
        </Section>

        <Section>
          <SectionTitle>11. International Data Transfers</SectionTitle>
          <Paragraph>
            Your information may be transferred to and processed in countries other than your own. We ensure that such transfers comply with applicable data protection laws and implement appropriate safeguards to protect your information.
          </Paragraph>
        </Section>

        <Section>
          <SectionTitle>12. Changes to This Policy</SectionTitle>
          <Paragraph>
            We may update this Privacy Policy from time to time. We will notify you of any material changes by:
          </Paragraph>
          <List>
            <ListItem>Posting the new policy on our website</ListItem>
            <ListItem>Sending email notifications to registered users</ListItem>
            <ListItem>Displaying in-app notifications</ListItem>
          </List>
          <Paragraph>
            Your continued use of the service after such changes constitutes acceptance of the updated policy.
          </Paragraph>
        </Section>

        <Section>
          <SectionTitle>13. Contact Us</SectionTitle>
          <Paragraph>
            If you have any questions about this Privacy Policy or our data practices, please contact us:
          </Paragraph>
          <Paragraph>
            <Highlight>Email:</Highlight> privacy@chefkats.ai<br />
            <Highlight>Data Protection Officer:</Highlight> dpo@chefkats.ai<br />
            <Highlight>Address:</Highlight> ChefKats.AI Privacy Team<br />
            <Highlight>Support:</Highlight> support@chefkats.ai
          </Paragraph>
        </Section>

        <LastUpdated>
          <LastUpdatedText>
            <strong>Last Updated:</strong> December 2024
          </LastUpdatedText>
        </LastUpdated>
      </Content>
    </Container>
  );
};

export default PrivacyPolicy;
